import { Button } from "@vds/buttons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { cancelInvitationEndPoint } from "../../configuration";
import axiosInstance from "../../global/axiosInterceptor";
import CustomHOCTable from "../../global/components/Tables/CustomHOCTable";
import { hasPermission } from "../../global/utility/permissionUtils";
import { validateEmail } from "../../global/utility/Utils";
import {
  showNotificationBanner
} from "../../store/actions";
import InviteUserModal from "../UserGroups/InviteUserModal";
import { DeleteInviteModal, ResendInviteModal } from "../UserGroups/UserGroupsModals";
import { createPendingUsersColumns } from "./PendingUsersTabColumns";
import { reinviteUser } from "../UserGroups/userGroupsService";
import { INVITE_USER_ERROR_MESSAGES, INVITE_USER_SUCCESS_MESSAGES } from "../../global/constants/constants";
import { PAGE_IDENTIFIERS } from "../../global/constants/tableConstants";
import { setDetailNavSource } from "../../hooks/usePaginationReset";
import { getDateforUsers } from "../../global/utility/Utils";


const PendingUsersTable = CustomHOCTable();

const PendingUsersTab = ({ users, usersList = [], onCountChange, onUserDetailsClick, onRefreshData, loading: parentLoading }) => {
  
  const [pendingUsersList, setPendingUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInviteUserModal, setShowInviteUserModal] = useState({
    isOpen: false,
    isLoading: false,
  });

  const { activeSession } = useSelector(
    (state) => state.sessionReducer
  );
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const { roleList, userRole } = useSelector((state) => state?.userRoleReducer);

  const getUserNameById = useCallback((userId) => {
    if (!userId || !usersList || usersList.length === 0) return userId || '';
    const foundUser = usersList.find(user => 
      user.id === userId || 
      user.userid === userId || 
      user.foreignid === userId
    );
    
    if (foundUser) {
      const name = `${foundUser.firstname || ''} ${foundUser.lastname || ''}`.trim() || foundUser.email || userId;
      return name;
    }
    return userId || '';
  }, [usersList]);
  const dispatch = useDispatch();

   const handleUserDetailsClick = useCallback((user) => {
    if (!hasPermission("User management", "Edit")) {
      dispatch(showNotificationBanner({
        type: "No Permission",
        priority: "critical",
        showNotification: true,
      }));
      return; 
    }

    setDetailNavSource(PAGE_IDENTIFIERS.PENDING_INVITES);
    onUserDetailsClick?.(user);
  }, [dispatch, onUserDetailsClick]);



  const canDeleteUser = (userRole) => {
    const currentUserRole = activeSession?.role || '';
    if (userRole === 'admin' && currentUserRole !== 'admin') {
      return false;
    }
    return true;
  };

  const handleOpenModal = () => {
    if (!hasPermission("User management", "Create")) {
      const notificationInfo = {
        type: "No Permission",
        priority: "critical",
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
    } else {
      setShowInviteUserModal({ ...showInviteUserModal, isOpen: true });
    }
  };

  const handleDelete = (user) => {
    if (!hasPermission("User management", "Delete")) {
      const notificationInfo = {
        type: "No Permission",
        priority: "critical",
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
      return;
    }
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleResendInvite = (user) => {
    if (!hasPermission("User management", "Edit")) {
      const notificationInfo = {
        type: "No Permission",
        priority: "critical",
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
      return;
    }
    setSelectedUser(user);
    setShowResendModal(true);
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
      try {
        const userId = selectedUser?.id || selectedUser?.foreignid || selectedUser?.userid;
        if (!userId) {
          throw new Error("User ID not found for deletion");
        }
        
        const response = await axiosInstance.post(cancelInvitationEndPoint(), {
          accountname: activeSession.accountId,
          resourceidentifier: {
            id: userId
          }
        });        
        if (response.status === 200) {
          const updatedList = pendingUsersList.filter(user => 
            (user.id || user.foreignid || user.userid) !== userId
          );
          setPendingUsersList(updatedList);
          if (onCountChange) {
            onCountChange(updatedList.length);
          }
          if (onRefreshData) {
            await onRefreshData();
          }
          const notificationInfo = {
            type: "Delete invite",
            priority: "success",
            data: {
              subTitle: `Invited user: ${selectedUser.User} has been deleted`,
            },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
        } else {
          throw new Error(`Failed to delete invitation. Status: ${response.status}`);
        }
        
      } catch (error) {
        const errorMessage = "Failed to delete pending invite. Please try again.";
        
        const notificationInfo = {
          type: "Invite not deleted",
          priority: "critical",
          data: {
            subTitle: errorMessage,
          },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
      }
  };

  const confirmResend = async () => {
    setShowResendModal(false);
    setResendingInvite(true);
    
    if (selectedUser) {
      try {
        const originalData = selectedUser.originalData || selectedUser;
        const email = originalData?.inviteeemail || originalData?.emailaddress || originalData?.email_address || selectedUser?.Email || '';
        if (!validateEmail(email)) {
          const notificationInfo = {
            type: "Resend Invite",
            priority: "critical",
            data: {
              subTitle: "Invalid email address. Cannot resend invite.",
            },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
          setSelectedUser(null);
          setResendingInvite(false);
          return;
        }

        const userId = originalData?.id || originalData?.foreignid || originalData?.userid;
        if (!userId) {
          throw new Error(INVITE_USER_ERROR_MESSAGES.userIdNotFoundForResend);
        }
        const firstname = originalData?.inviteefirstname || originalData?.firstname || '';
        const lastname = originalData?.inviteelastname || originalData?.lastname || '';
        const mdn = originalData?.inviteemdn || originalData?.mdn || '';
        const role = originalData?.assignment?.roleid || '';
        const tagids = originalData?.assignment?.siteids || [];
        const originalBillingAccounts = originalData?.billingaccountids || originalData?.assignment?.billingaccountids || [activeSession?.accountId];
        const billingAccountIds = originalBillingAccounts && originalBillingAccounts.length > 0 
          ? originalBillingAccounts 
          : [];
        
        if (!firstname || !lastname || !email) {
          throw new Error("Missing required user information for resending invite");
        }
        const reinvitePayload = {
          firstname,
          lastname,
          email,
          mdn: mdn || '',
          role,
          tagids,
          billingAccounts: billingAccountIds,
          oldinvitationid: userId
        };
        const resendResponse = await reinviteUser(reinvitePayload, activeSession, () => {}, () => {});
        if (resendResponse?.wasemailsent || resendResponse) {
          const userName = firstname && lastname ? `${firstname} ${lastname}` : email;
          const notificationInfo = {
            type: "Invite sent",
            priority: "success",
            data: {
              subTitle: INVITE_USER_SUCCESS_MESSAGES.resendInvite.replace('user_name', userName),
            },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));          
          if (onRefreshData) {
            await onRefreshData();
          }
        } else {
          dispatch(showNotificationBanner({
            type: "Resend Invite",
            priority: "warning",
            data: { subTitle: INVITE_USER_ERROR_MESSAGES.failedToSendInvitation },
            showNotification: true,
          }));
        }

      } catch (error) {
        const errorMessage = "Failed to resend invite. Please try again.";

        const notificationInfo = {
          type: "Resend Invite",
          priority: "critical",
          data: {
            subTitle: errorMessage,
          },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
      } finally {
        setResendingInvite(false);
      }
    } else {
      setResendingInvite(false);
    }
    setSelectedUser(null);
  };

  const columns = useMemo(() => {
    return createPendingUsersColumns({
      onUserDetailsClick: handleUserDetailsClick,
      getUserNameById,
      canDeleteUser,
      handleDelete,
      handleResendInvite,
      roleList,
      siteTagList,
      usersList
    });
  }, [handleUserDetailsClick, getUserNameById, canDeleteUser, handleDelete, handleResendInvite, roleList, siteTagList, usersList]);

  const fetchPendingUsersList = async () => {
    try {
      setLoading(true);
      const pendingUsers = users || [];
      setPendingUsersList(pendingUsers);
      if (onCountChange) {
        onCountChange(pendingUsers.length);
      }
    } catch (error) {
      setPendingUsersList([]);
      if (onCountChange) {
        onCountChange(0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUsersList();
  }, [users]);

  const getPendingUsersDataSource = (pendingUsersList) => {
    return pendingUsersList?.map((userItem) => {
      let roleObj = { id: null, name: 'Not Assigned' };
      let roleId = null;
      if (userItem?.assignment?.roleid && roleList && roleList.length > 0) {
        const role = roleList.find(r => r.id === userItem.assignment.roleid);
        if (role) {
          roleObj = { id: role.id, name: role.name };
          roleId = role.id; 
        }
      }

      let siteObj = { id: null, name: 'Not Available' };
      let siteIds = []; 
      let siteDisplayName = 'Not Available';
      if (userItem?.assignment?.siteids && userItem.assignment.siteids.length > 0 && siteTagList?.sites && siteTagList?.sites.length > 0) {
        const assignedSites = userItem.assignment.siteids
          .map(siteId => siteTagList?.sites.find(site => site.id === siteId))
          .filter(Boolean);
        
        if (assignedSites.length > 1) {
          siteDisplayName = `Multiple Sites (${assignedSites.length})`;
          siteObj = { id: assignedSites[0].id, name: siteDisplayName };
          siteIds = assignedSites.map(site => site.id); 
        } else if (assignedSites.length === 1) {
          siteObj = { id: assignedSites[0].id, name: assignedSites[0].name };
          siteDisplayName = assignedSites[0].name;
          siteIds = [assignedSites[0].id]; 
        }
      }

      return {
        id: userItem?.id || userItem?.foreignid || userItem?.userid,
        User: `${userItem?.inviteefirstname || userItem?.firstname || ''} ${userItem?.inviteelastname || userItem?.lastname || ''}`.trim() || userItem?.inviteeemail || 'Unknown User',
        Email: userItem?.inviteeemail || userItem?.emailaddress || userItem?.email_address || '',
        DateInvited: userItem?.createdon ? getDateforUsers(userItem) : '',
        AddedBy: (() => {
        const addedByValue = userItem?.invitedBy || userItem?.createdby || userItem?.hostuserid || '';
          return typeof addedByValue === 'string' || typeof addedByValue === 'number' ? addedByValue : '';
        })(),
        status: userItem?.isaccepted === false ? 'Pending' : userItem?.isaccepted === true ? 'Accepted' : 'Pending',
        Actions: "",
        role: roleObj, 
        site: siteObj, 
        siteIds: siteIds, 
        roleObj: roleObj,
        siteObj: siteObj,
        roleDisplay: roleObj.name,
        siteDisplay: siteDisplayName,
        originalData: userItem
      };
    });
  };

  const pendingUsersDataSource = useMemo(() => {
    return getPendingUsersDataSource(pendingUsersList);
  }, [pendingUsersList, roleList, siteTagList?.sites]);

  return (
    <>
      <InviteUserModal
        title="Invite User"
        showModal={showInviteUserModal.isOpen}
        setShowModal={setShowInviteUserModal}
        setShowNotificationPopup={dispatch}
        usersList={[...usersList, ...pendingUsersList]}
        onInviteSuccess={() => {
          if (onRefreshData) {
            onRefreshData();
          }
        }}
      />
      
      <PendingUsersTable
        showSearch={true}
        dataList={pendingUsersDataSource}
        columns={columns}
        loading={parentLoading || loading || resendingInvite}
        pageIdentifier={PAGE_IDENTIFIERS.PENDING_INVITES}
        useReduxPersistence={true}
        pagination={{ showSizeChanger: false }}
        extraAction={
          <Button
            size="medium"
            use="primary"
            className="responsive-action-button"
            onClick={handleOpenModal}
          >
            Invite User
          </Button>
        }
      />
      <DeleteInviteModal
        opened={showDeleteModal}
        onOpenedChange={setShowDeleteModal}
        selectedUser={selectedUser}
        confirmDelete={confirmDelete}
      />
      <ResendInviteModal
        opened={showResendModal}
        onOpenedChange={setShowResendModal}
        selectedUser={selectedUser}
        confirmResend={confirmResend}
      />
    </>
  );
};

export default PendingUsersTab;